import React, { useState } from 'react';
import OutlineButton from 'Components/Common/components/Button/OutlineButton';
import FillButton from 'Components/Common/components/Button/FillButton';
import Label from 'Components/Common/components/Label';
import ModalBody from 'Components/Common/Modal/ModalBody';
import ModalContainer from 'Components/Common/Modal/ModalContainer';
import ModalHeader from 'Components/Common/Modal/ModalHeader';
import { ModalFooter } from 'react-bootstrap';
import { AlertIcon } from 'assests/GmsIcons';
import { toast } from 'react-toastify';

type ListAction = "Validate" | "Import";

interface ValidationResponse {
    issueCount: number;
    url?: string;
}

type ValidationErrorModalProps = {
    validationError: ValidationResponse;
    onClose: () => void;
    action: ListAction;
};

const ValidationErrorModal = ({ validationError, onClose, action }: ValidationErrorModalProps) => {
    const [isDownloading, setIsDownloading] = useState(false);

    const handleDownload = () => {
        if (!validationError?.url) {
            toast.error("Download URL not available");
            return;
        }

        setIsDownloading(true);

        try {
            // Create a direct link for download
            const link = document.createElement('a');
            link.href = validationError.url;
            link.setAttribute('download', 'validation_report.xlsx');
            link.setAttribute('target', '_blank');
            document.body.appendChild(link);

            link.click();
            
            setTimeout(() => {
                toast.success("Download started");
                setIsDownloading(false);
                document.body.removeChild(link);
                onClose();
            }, 1000);
        } catch (error) {
            toast.error("Download failed. Please try again.");
            setIsDownloading(false);
        }
    };

    return (
        <ModalContainer isOpen={true} setOpen={onClose} width='500px'>
            <ModalHeader>
                <div>
                    <Label
                        message={action === "Validate" ? "Validate List" : "Import List"}
                        fontSize="0.875"
                        fontWeight="600"
                    />
                </div>
            </ModalHeader>
            <ModalBody>
                <div className="tw-px-4 tw-py-6 tw-flex tw-flex-col tw-items-center">
                    <div className="tw-px-4 tw-py-[5px] tw-bg-rose-100 tw-rounded tw-inline-flex tw-justify-center tw-items-center tw-gap-2.5">
                        <AlertIcon />
                        <div className="tw-text-center tw-justify-start">
                            <span className="tw-text-orange-700">Found</span>
                            <span className="tw-text-orange-700 tw-font-semibold"> {validationError?.issueCount} {validationError?.issueCount > 1 ? "Errors" : "Error"}</span>
                        </div>
                    </div>

                    <div className="tw-text-center tw-mb-6 tw-mt-10">
                        <p className="tw-text-gray-700">
                            *A new column 'Validation Errors' is added to the report mentioning all errors.
                        </p>
                        <p className="tw-text-gray-700 tw-mt-2">
                            Please fix errors and try validating again.
                        </p>
                    </div>

                    <div className="tw-flex tw-justify-center">
                        <FillButton
                            onClick={handleDownload}
                            className="tw-px-6"
                            disabled={isDownloading}
                        >
                            {isDownloading ? "Downloading..." : "Download Report"}
                        </FillButton>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <div className="tw-min-w-full tw-space-x-3 tw-justify-end tw-flex">
                    <OutlineButton onClick={() => onClose()}>Close</OutlineButton>
                </div>
            </ModalFooter>
        </ModalContainer>
    );
};

export default ValidationErrorModal;